// Firebase imports are now loaded dynamically

class AuthManager {
    constructor() {
        this.currentUser = null;
        this.isAuthenticated = false;
        
        // Asynchronously initialize auth listener
        this.initAuthListener();
    }

    async initAuthListener() {
        const { auth } = await import('./firebase.js');
        const { onAuthStateChanged } = await import('firebase/auth');

        // Listen for auth state changes globally
        onAuthStateChanged(auth, async (user) => {
            if (user) {
                this.currentUser = user;
                this.isAuthenticated = true;
                console.log("User Logged In:", user.email || user.phoneNumber);
                
                // Immediately sync XP & Leaderboard stats
                import('./state.js').then(({ stateManager }) => {
                    stateManager.syncUserToCloud();
                });
            } else {
                this.currentUser = null;
                this.isAuthenticated = false;
            }
        });
    }

    // --- EMAIL / PASSWORD ---
    
    async login(email, password) {
        try {
            const { auth } = await import('./firebase.js');
            const { signInWithEmailAndPassword } = await import('firebase/auth');
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            if (!userCredential.user.emailVerified) {
                const { signOut } = await import('firebase/auth');
                await signOut(auth);
                return { success: false, error: "Devi verificare la tua email prima di poter accedere. Controlla la tua casella di posta." };
            }
            return { success: true, user: userCredential.user };
        } catch (error) {
            console.error("Login Error:", error.code);
            let msg = "Errore durante il login.";
            if (error.code === 'auth/invalid-credential') msg = "Email o password errati.";
            if (error.code === 'auth/too-many-requests') msg = "Troppi tentativi. Riprova più tardi.";
            return { success: false, error: msg };
        }
    }

    async registerWithEmail(email, password, name, isAdult) {
        try {
            const { auth, db } = await import('./firebase.js');
            const { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } = await import('firebase/auth');
            const { doc, setDoc } = await import('firebase/firestore');

            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Update Auth Profile
            await updateProfile(user, { displayName: name });

            // Create Firestore Document with isAdult flag
            await setDoc(doc(db, "users", user.uid), {
                email: user.email,
                name: name,
                isAdult: isAdult,
                createdAt: new Date().toISOString()
            }, { merge: true });

            // Send Verification Email
            await sendEmailVerification(user);

            // Force sign out immediately so they have to verify
            const { signOut } = await import('firebase/auth');
            await signOut(auth);

            return { success: true, user, needsVerification: true };
        } catch (error) {
            console.error("Registration Error:", error);
            let msg = "Errore durante la registrazione.";
            if (error.code === 'auth/email-already-in-use') msg = "Email già in uso.";
            if (error.code === 'auth/weak-password') msg = "Password troppo debole (min 6 caratteri).";
            return { success: false, error: msg };
        }
    }

    // --- PHONE AUTH ---

    async setupRecaptcha(containerId) {
        const { auth } = await import('./firebase.js');
        const { RecaptchaVerifier } = await import('firebase/auth');
        
        // Use invisible reCAPTCHA
        window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
            'size': 'invisible',
            'callback': (response) => {
                // reCAPTCHA solved
            }
        });
        return window.recaptchaVerifier;
    }

    async loginWithPhone(phoneNumber, appVerifier) {
        try {
            const { auth } = await import('./firebase.js');
            const { signInWithPhoneNumber } = await import('firebase/auth');
            const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
            return { success: true, confirmationResult };
        } catch (error) {
            console.error("Phone Auth Error:", error);
            return { success: false, error: error.message };
        }
    }

    async verifyPhoneCode(confirmationResult, code, isAdult) {
        try {
            const { db } = await import('./firebase.js');
            const { doc, setDoc, getDoc } = await import('firebase/firestore');

            const result = await confirmationResult.confirm(code);
            const user = result.user;

            // Check if user document exists, if not create it (first login)
            const userRef = doc(db, "users", user.uid);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) {
                await setDoc(userRef, {
                    phoneNumber: user.phoneNumber,
                    isAdult: isAdult,
                    createdAt: new Date().toISOString()
                }, { merge: true });
            }

            return { success: true, user };
        } catch (error) {
            console.error("Code Verification Error:", error);
            return { success: false, error: "Codice errato o scaduto." };
        }
    }

    // --- UTILS ---

    async logout() {
        try {
            const { auth } = await import('./firebase.js');
            const { signOut } = await import('firebase/auth');
            await signOut(auth);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async resetPassword(email) {
        try {
            const { auth } = await import('./firebase.js');
            const { sendPasswordResetEmail } = await import('firebase/auth');
            await sendPasswordResetEmail(auth, email);
            return { success: true };
        } catch (error) {
            console.error("Reset Error:", error);
            return { success: false, error: error.message };
        }
    }

    getCurrentUser() {
        return this.currentUser;
    }
}

export const authManager = new AuthManager();
window.authManager = authManager;
